/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState } from 'react';
import SyncAllButton from '../SyncAllButton.tsx';
import SyncStatus from '../SyncStatus.tsx';
import AdminUnderConstruction from './AdminUnderConstruction.tsx';
import FeatureUsageStats from './FeatureUsageStats.tsx';
import ShopifySyncButton from '../ShopifySyncButton.tsx';
// import ShopifySyncAugustTestButton from '../ShopifySyncAugustTestButton.tsx';
import PrintfulSyncButton from '../PrintfulSyncButton.tsx';
import ShopifySyncStatus from '../ShopifySyncStatus.tsx';
import PrintfulSyncStatus from '../PrintfulSyncStatus.tsx';
import ShopifyWebhookStatus from './ShopifyWebhookStatus.tsx';
import PrintfulWebhookStatus from './PrintfulWebhookStatus.tsx';
import SyncOrderByNumber from './SyncOrderByNumber.tsx';
import SalesDataLastUpdated from '../SalesDataLastUpdated.tsx';
import CollectionsSyncButton from './CollectionsSyncButton.tsx';
import CollectionSyncStatus from './CollectionSyncStatus.tsx';
import AdminStatusSetter from './AdminStatusSetter.tsx';
import AdminUserLookup from './AdminUserLookup.tsx';
import AdminForceLogout from '../AdminForceLogout.tsx';
import CollectionHandlesManager from './CollectionHandlesManager.tsx';
import YoycolCostUpload from './YoycolCostUpload.tsx';
import BackfillPayoutHandlesButton from './BackfillPayoutHandlesButton.tsx';
import { AdminToolsProps } from '../../types/index.ts';
import './AdminTools.css';

const AdminTools = ({ userData }: AdminToolsProps) => {
  const [openSections, setOpenSections] = useState<string[]>(['']); // Default first section open
  const isAdmin = userData?.isAdmin;

  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section) 
        : [...prev, section]
    );
  };

  const isSectionOpen = (section: string) => openSections.includes(section);

  return (
    <div className="admin-tools">
      <div className="admin-tools__header">
        <h1>Admin Tools</h1>
        <p className="admin-tools__subtitle">Manage system data and user permissions</p>
      </div>

      <div className="admin-tools__container">
        {/* Data Synchronization Section */}
       <div className="admin-card padded">
  <div 
    className="admin-card__header admin-card__header--clickable" 
    onClick={() => toggleSection('force-logout')}
  >
    <div className="admin-card__header-content">
      <div className="align-center">
        <span className="admin-card__icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="100" height="100">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </span>
        <h2>Login Actions</h2>
      </div>
      <span className={`accordion-arrow ${isSectionOpen('force-logout') ? 'open' : ''}`}>
        ▼
      </span>
    </div>
  </div>
  
  {isSectionOpen('force-logout') && (
    <div className="admin-card__body">
        <div className="admin-card__actions">
            <div className="admin-card__panel">
                {isAdmin && <AdminForceLogout userIsAdmin={isAdmin} />}
            </div>
            <div className="admin-card__panel">
                {isAdmin && <AdminUnderConstruction userIsAdmin={isAdmin} />}
            </div>
        </div>
    </div>
  )}
</div>
        
        <div className="admin-card padded">
          <div 
            className="admin-card__header admin-card__header--clickable" 
            onClick={() => toggleSection('sync')}
          >
            <div className="admin-card__header-content">
              <div className="align-center">
                <span className="admin-card__icon">
                    
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="100" height="100">
                        <polyline points="17 1 21 5 17 9"/>
                        <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                        <polyline points="7 23 3 19 7 15"/>
                        <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                    </svg>

                </span>
                 <div>
                    <h2>Data Synchronization</h2>
                     <div className="accordion-header-status">{isAdmin && <SalesDataLastUpdated />}</div>
                 </div>
              </div>
              <span className={`accordion-arrow ${isSectionOpen('sync') ? 'open' : ''}`}>
                ▼
              </span>
            </div>
           
          </div>
          
          {isSectionOpen('sync') && (
            <div className="admin-card__body">
              {/* <p className="admin-card__description">
                Sync product data, sales information, and collections from Shopify and Printful APIs.
              </p> */}

              
              <div className="admin-card__actions">
                <div className="sync-group admin-card__panel">
                     <h3 className="text-lg font-semibold mb-2">Sync All Data</h3>
                     <SyncStatus />
                    <p>
                        Sync all collections and orders from Shopify and Printful. This may take several minutes.
                    </p>
                  <SyncAllButton isAdmin={isAdmin} />
                </div>
              
            {/* <div className="sync-group admin-card__panel">
                <h3>August Test Sync (temporary)</h3>
                <p>Backfills cost_per_item and shopify_shipping_charged onto August orders</p>
                <ShopifySyncAugustTestButton isAdmin={isAdmin} />
            </div> */}

                <div className="sync-group admin-card__panel">
                    <h3>Re-sync a Single Order</h3>
                    <p>
                      Look up one Shopify order by its order number and re-fetch it
                      immediately, without waiting for the next scheduled sync or webhook event.
                    </p>
                    {isAdmin && <SyncOrderByNumber />}
                </div>
                
                <div className="sync-group admin-card__panel">
                     <h3>Shopify Collections Sync</h3>
                     <CollectionSyncStatus />
                     <p>
                     Sync all collection names from Shopify to Firebase. This updates the list of collections available in the portal.
                     </p>
                     <CollectionsSyncButton isAdmin={isAdmin} />
                </div>
                 <div className="sync-group admin-card__panel">
                     <h3>Shopify Full Sync</h3>
                     <ShopifySyncStatus />
                     <p>
                       Sync all historical orders from Shopify to Firestore. This may take a few minutes.
                     </p>
                     <ShopifySyncButton isAdmin={isAdmin} />
                     <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                     <ShopifyWebhookStatus />
                     <p className="admin-card__description" style={{ fontSize: '12px', marginTop: '4px' }}>
                       Real-time updates from Shopify's orders/updated and orders/delete webhooks, independent of the full sync above.
                     </p>
                </div>

                <div className="sync-group admin-card__panel">
                    <h3>Printful Full Sync</h3>
                    <PrintfulSyncStatus />
                    <p>
                       Sync all historical orders from Printful to Firestore. This may take a few minutes.
                    </p>
                    <PrintfulSyncButton isAdmin={isAdmin} />
                    <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
                    <PrintfulWebhookStatus />
                    <p className="admin-card__description" style={{ fontSize: '12px', marginTop: '4px' }}>
                       Real-time updates from Printful's order webhook, independent of the full sync above.
                    </p>
                </div>

                <div className="sync-group admin-card__panel">
                    <h3>Upload to Yoycol</h3>
                    
                    <p>
                       Upload CSV file
                    </p>
                    <YoycolCostUpload isAdmin={isAdmin} />
                </div>

                <div className="sync-group admin-card__panel">
                    <h3>Backfill Payout Metadata</h3>
                    <p>
                       One-time fix for older payout records missing collectionHandle (makes
                       "Profit Paid" invisible to the seller it belongs to) or lastOrderNumberPaid
                       (the order-number reference point "amount earned since last payment" uses
                       instead of the free-text payout date). Safe to run more than once - fields
                       already present are left alone.
                    </p>
                    <BackfillPayoutHandlesButton isAdmin={isAdmin} />
                </div>

              </div>
            </div>
          )}
        </div>

        {/* User Management Section */}
        <div className="admin-card padded">
          <div 
            className="admin-card__header admin-card__header--clickable" 
            onClick={() => toggleSection('users')}
          >
            <div className="admin-card__header-content">
              <div className="align-center">
                <span className="admin-card__icon">

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="100" height="100">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>

                </span>
                <h2>User Management</h2>
              </div>
              <span className={`accordion-arrow ${isSectionOpen('users') ? 'open' : ''}`}>▼</span>
            </div>
          </div>
          
          {isSectionOpen('users') && (
            <div className="admin-card__body">
                <div className="admin-card__actions">
                    <div className="admin-card__panel">
                        <div className="admin-card__content">
                            {isAdmin && <CollectionHandlesManager userIsAdmin={isAdmin}/>}
                        </div>
                    </div>

                    <div className="admin-card__panel">
                        <div className="admin-card__content">
                            {isAdmin && <AdminUserLookup userIsAdmin={isAdmin} />}
                        </div>
                    </div>

                    <div className="admin-card__panel">
                        <div className="admin-card__content">
                            {isAdmin && <AdminStatusSetter userIsAdmin={isAdmin} />}
                        </div>
                    </div>
                </div>
               
            </div>
          )}
        </div>

        {/* Future sections - Analytics & Reports */}
        <div className="admin-card admin-card--disabled">
          <div className="admin-card__header">
            <div className="admin-card__header-content">
              <div className="align-center">
                <span className="admin-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="100" height="100">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                </span>
                <h2>Analytics & Reports</h2>
              </div>
              <span className="accordion-arrow disabled">▼</span>
            </div>
          </div>
          <div className="admin-card__body" style={{ display: 'block' }}>
            <p className="admin-card__description">
              Generate comprehensive reports and analytics. (Coming soon)
            </p>
            <FeatureUsageStats />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminTools;
